import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators, FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputText } from 'primeng/inputtext';
import { Dialog } from 'primeng/dialog';
import { Select } from 'primeng/select';
import { Pagination } from '@app/shared/components/api/models/pagination.model';
import { DataVoidComponent } from '@app/shared/components/data-void/data-void.component';
import { ConfirmationService, MessageService } from 'primeng/api';
import { PaginatorControlComponent } from '@app/shared/components/paginator-control/paginator-control.component';
import { TestemunhoItem, TestemunhoService, TestemunhoDto } from '@app/shared/components/api/services/testemunho.service';
import { UserService } from '@app/auth/services/user.service';
import { UtilizadoresService } from '@app/shared/components/api/services/utilizadores.service';

@Component({
  selector: 'app-testemunhos-config',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    ButtonModule,
    InputText,
    Dialog,
    Select,
    DataVoidComponent,
    PaginatorControlComponent
  ],
  providers: [
    TestemunhoService,
    UtilizadoresService
  ],
  templateUrl: './testemunhos-config.component.html',
  styleUrl: './testemunhos-config.component.scss'
})
export class TestemunhosConfigComponent implements OnInit {
  private readonly testemunhoService = inject(TestemunhoService);
  private readonly utilizadoresService = inject(UtilizadoresService);
  private readonly userService = inject(UserService);
  private readonly fb = inject(FormBuilder);
  private readonly messageService = inject(MessageService);
  private readonly confirmationService = inject(ConfirmationService);

  // Estados principais
  formTestemunho!: FormGroup;
  pagination = new Pagination<TestemunhoItem>();
  testemunhos: TestemunhoItem[] = [];
  loading: boolean = false;
  saving: boolean = false;

  // Modais
  showFormDialog: boolean = false;
  showDetailDialog: boolean = false;
  isEditing: boolean = false;
  selectedTestemunho: TestemunhoItem | null = null;

  // Filtros (exclusivos do Admin)
  filterText: string = '';
  filterEstrelas: number | null = null;
  filterAtivo: boolean | null = null;

  // Opções para Dropdowns de Filtro
  estrelasFilterOptions = [
    { label: 'Todas as Estrelas', value: null },
    { label: '5 Estrelas', value: 5 },
    { label: '4 Estrelas', value: 4 },
    { label: '3 Estrelas', value: 3 },
    { label: '2 Estrelas', value: 2 },
    { label: '1 Estrela', value: 1 }
  ];

  ativoFilterOptions = [
    { label: 'Todos os Estados', value: null },
    { label: 'Apenas Ativos', value: true },
    { label: 'Apenas Inativos', value: false }
  ];

  // Lista de utilizadores para vincular
  utilizadoresOptions: { label: string; value: string }[] = [];

  ngOnInit(): void {
    this.initForm();
    this.loadUtilizadores();
    this.loadTestemunhos();
  }

  private initForm(): void {
    this.formTestemunho = this.fb.group({
      id: [0],
      utilizadorID: [this.userService.getUserId() || '', [Validators.required]],
      estrelas: [5, [Validators.required, Validators.min(1), Validators.max(5)]],
      descricao: ['', [Validators.required, Validators.minLength(5), Validators.maxLength(1000)]]
    });
  }

  loadUtilizadores(): void {
    this.utilizadoresService.list({ PageSize: 100 }).subscribe({
      next: (res) => {
        const users = res?.data || [];
        this.utilizadoresOptions = users.map((u: any) => ({
          label: `${u.nome || u.userName || 'Sem nome'} (${u.email || u.id})`,
          value: u.id
        }));

        // Se o usuário logado não estiver na lista (ex: token admin), adiciona-o como opção
        const currentUserId = this.userService.getUserId();
        if (currentUserId && !this.utilizadoresOptions.some(opt => opt.value === currentUserId)) {
          this.utilizadoresOptions.unshift({
            label: `Meu Utilizador (${this.userService.getUsername() || 'Admin'})`,
            value: currentUserId
          });
        }
      },
      error: () => {
        // Fallback garantido se falhar
        const currentUserId = this.userService.getUserId();
        if (currentUserId) {
          this.utilizadoresOptions = [{
            label: `Meu Utilizador (${this.userService.getUsername() || 'Admin'})`,
            value: currentUserId
          }];
        }
      }
    });
  }

  loadTestemunhos(page: number = 1): void {
    this.loading = true;

    const params: any = {
      PageSize: this.pagination.pageSize || 10,
      PageNumber: page,
      OrderBy: 'id desc'
    };

    if (this.filterText?.trim()) {
      params.Descricao = this.filterText.trim();
    }

    if (this.filterEstrelas !== null && this.filterEstrelas !== undefined) {
      params.Estrelas = this.filterEstrelas;
    }

    if (this.filterAtivo !== null && this.filterAtivo !== undefined) {
      params.Ativo = this.filterAtivo;
    }

    this.testemunhoService.list(params).subscribe({
      next: (res) => {
        this.pagination = new Pagination<TestemunhoItem>().deserialize(res);
        this.testemunhos = this.pagination.data || [];
        this.loading = false;
      },
      error: (err) => {
        console.error('Erro ao carregar testemunhos:', err);
        this.messageService.add({
          severity: 'error',
          summary: 'Erro',
          detail: 'Não foi possível carregar os testemunhos.'
        });
        this.testemunhos = [];
        this.loading = false;
      }
    });
  }

  applyFilter(): void {
    this.loadTestemunhos(1);
  }

  clearFilters(): void {
    this.filterText = '';
    this.filterEstrelas = null;
    this.filterAtivo = null;
    this.loadTestemunhos(1);
  }

  onPageChange(event: any): void {
    const page = Math.floor(event.first / event.rows) + 1;
    this.pagination.pageSize = event.rows;
    this.loadTestemunhos(page);
  }

  // --- Criação e Edição ---
  openCreateDialog(): void {
    this.isEditing = false;
    this.formTestemunho.reset({
      id: 0,
      utilizadorID: this.userService.getUserId() || (this.utilizadoresOptions[0]?.value || ''),
      estrelas: 5,
      descricao: ''
    });
    this.showFormDialog = true;
  }

  openEditDialog(item: TestemunhoItem): void {
    this.isEditing = true;
    this.formTestemunho.patchValue({
      id: item.id,
      utilizadorID: item.utilizadorID || this.userService.getUserId() || '',
      estrelas: item.estrelas || 5,
      descricao: item.descricao || ''
    });
    this.showFormDialog = true;
  }

  closeFormDialog(): void {
    this.showFormDialog = false;
    this.formTestemunho.reset();
  }

  setStars(stars: number): void {
    this.formTestemunho.patchValue({ estrelas: stars });
  }

  saveTestemunho(): void {
    if (this.formTestemunho.invalid) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Formulário Inválido',
        detail: 'Por favor preencha todos os campos obrigatórios.'
      });
      this.formTestemunho.markAllAsTouched();
      return;
    }

    this.saving = true;
    const formValue = this.formTestemunho.value;

    const payload: TestemunhoDto = {
      id: this.isEditing ? formValue.id : 0,
      utilizadorID: formValue.utilizadorID,
      estrelas: Number(formValue.estrelas),
      descricao: formValue.descricao
    };

    const action$ = this.isEditing
      ? this.testemunhoService.update(payload)
      : this.testemunhoService.store(payload);

    action$.subscribe({
      next: (res) => {
        this.saving = false;
        this.showFormDialog = false;
        this.messageService.add({
          severity: 'success',
          summary: 'Sucesso',
          detail: this.isEditing ? 'Testemunho atualizado com sucesso!' : 'Testemunho criado com sucesso!'
        });
        this.loadTestemunhos(this.pagination.pageNumber || 1);
      },
      error: (err) => {
        this.saving = false;
        console.error('Erro ao guardar testemunho:', err);
        this.messageService.add({
          severity: 'error',
          summary: 'Erro',
          detail: err?.error?.retorno?.mensagem || 'Erro ao guardar o testemunho.'
        });
      }
    });
  }

  // --- Ver Detalhes ---
  openDetailDialog(item: TestemunhoItem): void {
    this.selectedTestemunho = item;
    this.showDetailDialog = true;
  }

  closeDetailDialog(): void {
    this.showDetailDialog = false;
    this.selectedTestemunho = null;
  }

  // --- Ativar / Desativar (Toggle) ---
  toggleStatus(item: TestemunhoItem): void {
    const acao = item.ativo ? 'desativar' : 'ativar';
    this.confirmationService.confirm({
      message: `Tem a certeza que deseja ${acao} este testemunho?`,
      header: 'Confirmação de Estado',
      icon: 'pi pi-exclamation-triangle',
      rejectButtonProps: {
        label: 'Cancelar',
        severity: 'secondary',
        outlined: true,
      },
      acceptButtonProps: {
        label: `Sim, ${acao.charAt(0).toUpperCase() + acao.slice(1)}`,
        severity: item.ativo ? 'danger' : 'success'
      },
      accept: () => {
        this.testemunhoService.toggleStatus(item.id).subscribe({
          next: () => {
            this.messageService.add({
              severity: 'success',
              summary: 'Estado Atualizado',
              detail: `Testemunho ${item.ativo ? 'desativado' : 'ativado'} com sucesso!`
            });
            this.loadTestemunhos(this.pagination.pageNumber || 1);
          },
          error: (err) => {
            console.error('Erro ao alterar status:', err);
            this.messageService.add({
              severity: 'error',
              summary: 'Erro',
              detail: 'Não foi possível alterar o estado do testemunho.'
            });
          }
        });
      }
    });
  }

  // --- Eliminar ---
  deleteTestemunho(item: TestemunhoItem): void {
    this.confirmationService.confirm({
      message: `Tem a certeza que deseja eliminar permanentemente este testemunho?`,
      header: 'Eliminar Testemunho',
      icon: 'pi pi-trash',
      rejectButtonProps: {
        label: 'Cancelar',
        severity: 'secondary',
        outlined: true,
      },
      acceptButtonProps: {
        label: 'Sim, Eliminar',
        severity: 'danger'
      },
      accept: () => {
        this.testemunhoService.delete(item.id).subscribe({
          next: () => {
            this.messageService.add({
              severity: 'success',
              summary: 'Eliminado',
              detail: 'Testemunho eliminado com sucesso!'
            });
            this.loadTestemunhos(1);
          },
          error: (err) => {
            console.error('Erro ao eliminar:', err);
            this.messageService.add({
              severity: 'error',
              summary: 'Erro',
              detail: 'Não foi possível eliminar o testemunho.'
            });
          }
        });
      }
    });
  }

  // Helpers
  getStarsArray(count: number): number[] {
    return Array(Math.min(5, Math.max(0, count || 0))).fill(0);
  }

  getEmptyStarsArray(count: number): number[] {
    return Array(Math.max(0, 5 - Math.min(5, Math.max(0, count || 0)))).fill(0);
  }

  getUserInitials(name?: string): string {
    if (!name?.trim()) return 'FC';
    const parts = name.trim().split(' ').filter(Boolean);
    if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
}
